function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// @ts-strict-ignore
import React, { useEffect, useImperativeHandle, useRef } from 'react';
import { clsx } from 'clsx';
import { Visibility, VisibilityEvents } from 'common/components/base/stackable';
import { SvgIconReact, SvgIconType } from 'common/components/svg-icon';
import { NewTooltip } from 'common/components/tooltip/new-tooltip';
import { DisplaySize } from 'common/components/tooltip/types';
import { MetricType } from 'common/types/reports/bookmark';
import { RowActions } from 'common/query-builder/query-entry-section/components/actions';
import { RowLabel } from 'common/query-builder/query-entry-section/components/row-label';
import { Action, DROP_POSITION, Entry, EntryPart, isMetricEntry, MetricEntry, QueryEventEntry, RowLabelType } from 'common/query-builder/query-entry-section/types';
import { ReportName } from 'common/report/constants';
import { useDragAndDropManager } from 'common/contexts/react/drag-and-drop-manager-context';
import { SectionItem } from 'common/components/items-menu/types';
import { MetricUnitsButton } from 'iron/common/query-builder/chart-customization/metric-units';
import { CallingLocation } from 'common/query-builder/chart-customization/tracking';
import { ControlBarActionItem, ControlBarActionItems } from '../constants';
import { QueryEntrySectionTrackingEvent } from '../tracking';
import { EntryRowProps } from './types';
type Props = EntryRowProps<Entry> & {
  onDeleteEntry?: () => void;
  extraEntryClass?: Record<string, boolean>;
  hideActions?: boolean;
  isPlaceholder: boolean;
  renderActions?: () => React.ReactNode;
  renderBody?: () => React.ReactNode;
  renderSelector: (props: {
    rowIcon: SvgIconType | null;
    rowActions: React.ReactNode;
  }) => React.ReactNode;
  canAlwaysDelete?: boolean;
  overflowControls: ControlBarActionItem[];
  entryIcon?: SvgIconType;
  showLabels?: boolean;
  /** If an entry is disabled, it will be greyed out and readonly. */
  disabled?: boolean;
  /** Tooltip to display for the whole row. */
  tooltip?: string;
};

/**
 * BaseEntryRow provides the basic elements for every entry type
 * and provides render props for the [selector] and [body rows]
 * and [suffix] from the following layout:
 *
 * ===============================================================
 * [drag] [label] | [entry-icon] [ selector ] [actions] [overflow]
 *                | [    body (filters, etc)                     ]
 *                | [    suffix (measurement, etc)               ]
 * ===============================================================
 */
export const BaseEntryRow = React.forwardRef<HTMLDivElement, Props>((props, forwardedRef) => {
  var _props$showLabels, _props$extraEntryClas, _props$onDeleteEntry, _props$entry$filters, _props$placeholder;
  const dragAndDropManager = useDragAndDropManager();
  const ref = useRef<HTMLDivElement>(null);
  useImperativeHandle(forwardedRef, () => ref.current);
  const isMetricEntryRow = isMetricEntry(props.entry);
  const {
    entryKey
  } = props.entry;
  const dropAreas = props.getDropAreas();
  const dropAllowedAbove = dropAreas.includes(DROP_POSITION.ABOVE);
  const dropAllowedBelow = dropAreas.includes(DROP_POSITION.BELOW) && !props.disabled;
  const dropAllowedOn = dropAreas.includes(DROP_POSITION.ON) && !props.disabled;
  const draggable = props.isDraggingEnabled();
  const isSectionDraggable = !props.disabled && props.sectionProps.rowDraggable && props.sectionProps.rowLimit !== 1;
  const isDraggingAnything = !!dragAndDropManager.draggingEl;
  const isDraggableAnywhere = draggable && props.hasNewUI;
  const entries = props.getEntries(true);
  let isDropzoneEnabled = isDraggingAnything && dropAreas.length > 0;
  if (props.sectionProps.reportName === ReportName.Funnels && entries.length === 1) {
    isDropzoneEnabled = isDropzoneEnabled && !isMetricEntryRow;
  }
  let isNotDropArea = isDraggingAnything && dropAreas.length === 0;
  if (props.sectionProps.reportName === ReportName.Funnels && entries.length === 1) {
    isNotDropArea = isNotDropArea && !isMetricEntryRow;
  }
  const shouldRenderRowActions = !props.hasNewUI && !!props.sectionProps.listProperty && props.index === 0;
  const hasActions = !props.sectionProps.readonly && (!props.hideActions || shouldRenderRowActions);
  const showLabels = (_props$showLabels = props.showLabels) !== null && _props$showLabels !== void 0 ? _props$showLabels : props.sectionProps.rowLabelType !== RowLabelType.None;

  // Only show metrics unit button on top-level entry
  const shouldShowMetricUnitsButton = props.showDefaultMetricUnitSelector && !props.isNestedSection && props.nestedSectionParams.reportName && (
  // Only show if the entry has a measurement or event
  (props.entry as MetricEntry).measurement || (props.entry as QueryEventEntry).event) && (props.sectionProps.reportName === ReportName.Funnels || props.sectionProps.reportName === ReportName.Retention ? (_props$extraEntryClas = props.extraEntryClass) === null || _props$extraEntryClas === void 0 ? void 0 : _props$extraEntryClas["is-metric"] : true);
  const entryIndexLabelToggle = "behavior" in props.entry ? props.entry.behavior.entryIndexLabelToggle : props.entry.entryIndexLabelToggle;
  const rowIcon = entryIndexLabelToggle && props.sectionProps.rowIndexToggleIcon ? props.sectionProps.rowIndexToggleIcon : null;
  useEffect(() => {
    var _ref$current, _ref$current2, _ref$current3;
    const onDropdownOpen = () => props.onDropdownStateChanged(true);
    const onDropdownClose = () => props.onDropdownStateChanged(false);
    const onContextMenuClose = (ev: Event) => {
      props.onDropdownStateChanged(false);
      props.propagateEvent(ev);
    };
    (_ref$current = ref.current) === null || _ref$current === void 0 || _ref$current.addEventListener(VisibilityEvents[Visibility.Open], onDropdownOpen);
    (_ref$current2 = ref.current) === null || _ref$current2 === void 0 || _ref$current2.addEventListener(VisibilityEvents[Visibility.Closed], onDropdownClose);
    (_ref$current3 = ref.current) === null || _ref$current3 === void 0 || _ref$current3.addEventListener("closeContextMenu", onContextMenuClose);
    return () => {
      var _ref$current4, _ref$current5, _ref$current6;
      (_ref$current4 = ref.current) === null || _ref$current4 === void 0 || _ref$current4.removeEventListener(VisibilityEvents[Visibility.Open], onDropdownOpen);
      (_ref$current5 = ref.current) === null || _ref$current5 === void 0 || _ref$current5.removeEventListener(VisibilityEvents[Visibility.Closed], onDropdownClose);
      (_ref$current6 = ref.current) === null || _ref$current6 === void 0 || _ref$current6.removeEventListener("closeContextMenu", onContextMenuClose);
    };
  }, []);
  const onLabelClick = () => {
    if (props.sectionProps.rowIndexToggleable && !isMetricEntry(props.entry)) {
      props.updateEntry(Action.Edit, _objectSpread(_objectSpread({}, props.entry), {}, {
        entryIndexLabelToggle: !props.entry.entryIndexLabelToggle
      }));
      props.dispatchTrack(QueryEntrySectionTrackingEvent.EventIndexLabelClicked, _objectSpread(_objectSpread({}, props.getEntryTrackingProps()), {}, {
        'Previous Label': props.rowLabel
      }));
    }
  };
  const rowActions = shouldRenderRowActions ? props.sectionProps.rowActions : <RowActions entry={props.entry} hasNewUI={props.hasNewUI} hasBehavioralFrequencyFilter={props.hasBehavioralFrequencyFilter} overflowControls={props.overflowControls} overflowControlsContextPanelTransform={(item: SectionItem) => {
    var _ControlBarActionItem;
    return (_ControlBarActionItem = ControlBarActionItems[item === null || item === void 0 ? void 0 : item.value]) === null || _ControlBarActionItem === void 0 ? void 0 : _ControlBarActionItem.tooltipData;
  }} isEmptyPlaceholder={props.isEmptyPlaceholder()} canAlwaysDeleteEntry={props.canAlwaysDelete || false} canDeleteEntry={props.canDelete} disabled={props.sectionProps.disabled} onClickDelete={(_props$onDeleteEntry = props.onDeleteEntry) !== null && _props$onDeleteEntry !== void 0 ? _props$onDeleteEntry : props.removeEntry} onControlSelect={props.onControlSelect} renderActions={props.renderActions} />;
  const draggingControl = isSectionDraggable && <NewTooltip content='Drag to move' size={DisplaySize.Small} placement='top' enabled={props.hasNewUI && !(props.isDraggingGripper || isDraggingAnything)}>
      <div className={clsx("mp-query-entry-gripper", {
      'is-placeholder': props.isPlaceholder,
      'is-dragging-disabled': !draggable
    })} onMouseDown={props.onGripperMouseDown}>
        <SvgIconReact icon={props.hasNewUI ? "gripper-xs" : "gripper"} />
      </div>
    </NewTooltip>;
  return <NewTooltip content={props.tooltip} size={DisplaySize.Small} placement='left'>
      <div ref={ref} className={clsx("mp-query-entry", {
      'drop-indicator-above': dropAllowedAbove && props.isDroppingAbove,
      'drop-indicator-below': dropAllowedBelow && props.isDroppingBelow,
      'drop-indicator-on': dropAllowedOn && props.isDroppingOn,
      'indent-drop-indicator': props.isNestedSection,
      'is-placeholder': props.isPlaceholder,
      'is-draggable': isSectionDraggable,
      'is-hidden': props.entry.isHidden,
      'is-draggable-anywhere': isDraggableAnywhere,
      'is-entry-selector-dropdown-open': props.isEntrySelectorDropdownOpen,
      'is-not-a-drop-area': isNotDropArea,
      'is-disabled': props.disabled,
      'has-actions': hasActions,
      'has-labels': Boolean(showLabels || rowIcon),
      'has-filters': isMetricEntryRow ? false : "filters" in props.entry && ((_props$entry$filters = props.entry.filters) === null || _props$entry$filters === void 0 ? void 0 : _props$entry$filters.length) > 0 || ((_props$placeholder = props.placeholder) === null || _props$placeholder === void 0 ? void 0 : _props$placeholder.includes(EntryPart.Filters)),
      'is-being-dragged': props.isDragging,
      ["is-".concat(props.entry.type)]: true,
      'is-exclusion-event': props.isExclusionStepsSection
    }, props.extraEntryClass)} key={entryKey} elref='entryRow' draggable={draggable}
    // @ts-ignore
    disabled={props.sectionProps.disabled} onDragEnd={props.onEntryStepDragClear} onMouseDown={isDraggableAnywhere ? props.onGripperMouseDown : undefined} onDragStart={draggable ? props.onEntryStepDragStart : undefined} onDragEnter={draggable ? props.onEntryStepDragEnter : undefined} onDragOver={isDropzoneEnabled ? props.onEntryStepDragOver : undefined} onDragLeave={isDropzoneEnabled ? props.onEntryStepDragLeave : undefined} onDrop={isDropzoneEnabled ? props.onEntryStepDrop : undefined}>
        {/** The drop zones make drag and drop more forgiving by increasing the area with hidden elements */}
        {props.hasNewUI && isDropzoneEnabled && <>
            <div className={clsx("drop-zone-top", "drop-zone", {
          'is-first': props.index === 0 && !Number.isInteger(props.sectionProps.rowIndex)
        })} />
            <div className={clsx("drop-zone-bottom", "drop-zone", {
          'is-last': props.index === entries.length - 1 && !Number.isInteger(props.sectionProps.rowIndex)
        })} />
          </>}
        {/** prefix row */}
        {props.renderPrefix && props.renderPrefix({
        index: props.index,
        entry: props.entry
      })}

        {/** dragging control */}
        {!props.hasNewUI && draggingControl}

        <div className={clsx("mp-query-entry-label-wrapper", {
        'has-label': showLabels,
        'has-metric-units-button': shouldShowMetricUnitsButton
      })}>
          {/** optional row label */}
          {showLabels && <RowLabel index={props.index} entry={props.entry} isPlaceholder={props.isPlaceholder} reportName={isMetricEntry(props.entry) ? props.entry.behavior.type === MetricType.Funnel ? ReportName.Funnels : ReportName.Retention : props.sectionProps.reportName} hasNewUI={props.hasNewUI} isReferencedMetrics={props.sectionProps.isReferencedMetrics} rowIcon={rowIcon} rowLabel={props.rowLabel} rowLabelTheme={props.rowLabelTheme}
        // those props are special case for funnel event comparison & anyorder
        // this is the layout:
        // [rowLabelPrefix] [rowLabel] <event_name> [actions] [overflow]
        //  ^--- prefix is togglable to "any-order" icon when click if it's anyorder funnel
        onGroupClick={props.onGroupLabelClick} onClick={onLabelClick} rowIndexToggleable={props.sectionProps.rowIndexToggleable} rowLabelPrefix={props.sectionProps.rowLabelPrefix} rowLabelPrefixIcon={props.sectionProps.rowLabelPrefixIcon} />}
          {props.entry && shouldShowMetricUnitsButton && <div className={"mp-query-entry-sublabel"}>
              <MetricUnitsButton callingLocation={CallingLocation.Metric} chartType={props.currentChartType} setEntry={entry => props.updateEntry(Action.Edit, entry as Entry, {
            requery: false
          })} showDefaultMetricUnitSelector={props.showDefaultMetricUnitSelector} insideFooter entry={props.entry} />
            </div>}
        </div>

        {/* entry icon */}
        {/* entry icon is used by filter, breakdown and metric-entry(funnel,retention) */}
        {/* it's different from <RowLabel />, which is used to display formula prefix  */}
        {props.hasNewUI && props.entryIcon && <div className='mp-query-entry-icon'>
            <SvgIconReact icon={props.entryIcon} />
          </div>}

        <div className='mp-query-entry-body'>
          {/* There are cases we may not have a selector since it exists elsewhere (e.g. a filter for single segment) */}
          {props.renderSelector && <div className={clsx("mp-query-entry-row", "for-selector", {
          'has-label': showLabels,
          'is-placeholder': props.isPlaceholder
        })} elref='queryEntryRow'>
              {/** main selector body */}
              {/* rowActions is passed to selector renderer so that it can render the row's actions elsewhere. this is only used by old QB complex list filters */}
              {props.renderSelector({
            rowActions,
            rowIcon
          })}

              {/** row actions */}
              {hasActions && rowActions}
            </div>}

          {/** body rows */}
          {props.renderBody && props.renderBody()}
        </div>

        {/** suffix row */}
        {props.renderSuffix && props.renderSuffix({
        index: props.index,
        entry: props.entry
      })}

        {/** dragging control */}
        {props.hasNewUI && draggingControl}
      </div>
    </NewTooltip>;
});
BaseEntryRow.displayName = "BaseEntryRow";